import {
  payoutDownloadSchema,
  payoutEntrySchema,
  payoutListSchema,
  payoutOverviewSchema,
  payoutSchema,
  staffLedgerSchema,
  type Payout,
  type PayoutDownload,
  type PayoutEntry,
  type PayoutList,
  type PayoutOverview,
  type RecordAdvanceInput,
  type RecordPayoutAdjustmentInput,
  type SendStatementInput,
  type SettlePayoutInput,
  type StaffLedger,
} from '@tailoring-crm/shared';
import type { ApiClient } from '../api/client.js';

/**
 * Every wage call the app makes.
 *
 * Note the shape of the two read paths. `/payouts/me` takes no id and never will: the karigar's
 * own screen asks a question that can only be answered about the caller, so there is no request
 * this client can build that would return somebody else's earnings — the server pins it to the
 * session. `/payouts/staff/:id` is the owner's settlement screen, and the server refuses it to
 * anybody asking about a person who is not themselves.
 *
 * Every response is parsed against the shared schema rather than cast. On a screen that decides
 * what cash to hand over, a server that broke the contract must fail loudly rather than render a
 * missing figure as blank.
 */

const PAYOUTS = '/payouts';

export interface PeriodParams {
  /** `YYYY-MM-DD`, as a date input produces it. Omit for everything up to today. */
  from?: string;
  to?: string;
}

function periodQuery(params: PeriodParams): Record<string, string> {
  return {
    ...(params.from ? { from: params.from } : {}),
    ...(params.to ? { to: params.to } : {}),
  };
}

/** Who is owed what, across the shop, plus the total wage liability. */
export async function getPayoutOverview(
  client: ApiClient,
  params: PeriodParams = {},
): Promise<PayoutOverview> {
  return await client.get(`${PAYOUTS}/overview`, {
    schema: payoutOverviewSchema,
    query: periodQuery(params),
  });
}

/** One person's ledger — the settlement screen's whole payload. */
export async function getStaffLedger(
  client: ApiClient,
  staffUserId: string,
  params: PeriodParams = {},
): Promise<StaffLedger> {
  return await client.get(`${PAYOUTS}/staff/${staffUserId}`, {
    schema: staffLedgerSchema,
    query: periodQuery(params),
  });
}

/** The caller's own earnings. No id, by design. */
export async function getMyEarnings(
  client: ApiClient,
  params: PeriodParams = {},
): Promise<StaffLedger> {
  return await client.get(`${PAYOUTS}/me`, {
    schema: staffLedgerSchema,
    query: periodQuery(params),
  });
}

export async function getPayouts(
  client: ApiClient,
  params: PeriodParams & { staffUserId?: string } = {},
): Promise<PayoutList> {
  return await client.get(PAYOUTS, {
    schema: payoutListSchema,
    query: {
      ...periodQuery(params),
      ...(params.staffUserId ? { staffUserId: params.staffUserId } : {}),
    },
  });
}

export async function getPayout(client: ApiClient, payoutId: string): Promise<Payout> {
  return await client.get(`${PAYOUTS}/${payoutId}`, { schema: payoutSchema });
}

export async function settlePayout(
  client: ApiClient,
  staffUserId: string,
  input: SettlePayoutInput,
): Promise<Payout> {
  return await client.post(`${PAYOUTS}/staff/${staffUserId}/settle`, input, {
    schema: payoutSchema,
  });
}

export async function recordAdvance(
  client: ApiClient,
  staffUserId: string,
  input: RecordAdvanceInput,
): Promise<PayoutEntry> {
  return await client.post(`${PAYOUTS}/staff/${staffUserId}/advances`, input, {
    schema: payoutEntrySchema,
  });
}

export async function recordAdjustment(
  client: ApiClient,
  staffUserId: string,
  input: RecordPayoutAdjustmentInput,
): Promise<PayoutEntry> {
  return await client.post(`${PAYOUTS}/staff/${staffUserId}/adjustments`, input, {
    schema: payoutEntrySchema,
  });
}

/** A short-lived link to the statement PDF. The server renders it if it is not there yet. */
export async function getStatementDownload(
  client: ApiClient,
  payoutId: string,
): Promise<PayoutDownload> {
  return await client.get(`${PAYOUTS}/${payoutId}/statement`, { schema: payoutDownloadSchema });
}

export async function sendStatement(
  client: ApiClient,
  payoutId: string,
  input: SendStatementInput = {},
): Promise<Payout> {
  return await client.post(`${PAYOUTS}/${payoutId}/send`, input, { schema: payoutSchema });
}
